import { CSSProperties, FC, useId, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Tooltip } from '@platform/ui-tooltip'
import styles from './CourseResultBadge.module.scss'
import { CourseResultBadgeTooltipContent } from './CourseResultBadgeTooltipContent/CourseResultBadgeTooltipContent'

type Props = {
  label: string
  className?: string
  dotStyles: CSSProperties
  grade?: string
  isPassed?: boolean
  state?: string
  score?: string | null
  showPassed?: boolean
}

export const CourseResultBadge: FC<Props> = ({
  grade,
  className,
  label,
  dotStyles,
  isPassed,
  score,
  state,
  showPassed,
}) => {
  const { t, i18n } = useTranslation()

  const badgeLabel = useMemo(() => {
    if (grade) {
      const localized = i18n.exists(`grade.${grade}`, { defaultValue: grade })
      const gradeLabel = localized ? t(`grade.${grade}`, { defaultValue: grade }) : grade

      return `${t('badge.result')}: ${gradeLabel} (${label})`
    }

    if (label === t('courseDetails.gradingInProgress')) {
      return label
    }

    // label: Passed, Failed
    const localized = i18n.exists(`grade.${label}`, { defaultValue: label })
    const gradeLabel = localized ? t(`grade.${label}`, { defaultValue: label }) : label

    return `${t('badge.result')}: ${gradeLabel}`
  }, [grade, i18n, label, t])

  const id = useId()

  if (!grade && !score && !state) {
    return (
      <div className={className}>
        <div className={styles.badge}>
          <span className={styles.dot} style={dotStyles} />
          {badgeLabel}
        </div>
      </div>
    )
  }

  return (
    <div className={className}>
      <Tooltip
        render={() => (
          <CourseResultBadgeTooltipContent
            showPassed={showPassed}
            isPassed={isPassed}
            grade={grade}
            score={score}
            state={state}
          />
        )}
        place="top-start"
        id={id}
      />

      <div className={styles.badge} data-tooltip-id={id}>
        <span className={styles.dot} style={dotStyles} />
        {badgeLabel}
      </div>
    </div>
  )
}
